import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGoBack } from "@/lib/useGoBack";
import { MapPin, Clock, DollarSign, Dumbbell, BookmarkPlus, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import SocialsDisplay from "@/components/SocialsDisplay";
import MapLink from "@/components/MapLink";
import ShareButton from "@/components/ShareButton";
import MessageBoard from "@/components/MessageBoard";
import CollaboratorsManager from "@/components/CollaboratorsManager";
import FlagButton from "@/components/FlagButton";
import DistanceZip from "@/components/DistanceZip";
import { savedNearZip, getSavedFilters, saveFilters } from "@/lib/filterCookies";
import { Button } from "@/components/ui/button";
import useFitOneLine from "@/lib/useFitOneLine";
import HeroLogoBadge from "@/components/HeroLogoBadge";
import FollowAlertsBell from "@/components/FollowAlertsBell";

export default function TrainerDetail() {
    const { id } = useParams();
    const goBack = useGoBack();
    const { user, isFollowing, toggleFollow } = useAuth();
    const [trainer, setTrainer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [followBusy, setFollowBusy] = useState(false);
    const [zipState, setZipState] = useState(savedNearZip());
    const [coords, setCoords] = useState(null);  // precise device lat/lng, when granted
    const nameFitRef = useFitOneLine(trainer?.name || "", { max: 48 });
    const handleZip = (z, c) => {
        setZipState(z);
        setCoords(c || null);
        saveFilters({ ...getSavedFilters({}), near_zip: z });
    };

    useEffect(() => {
        const params = {};
        if (coords) { params.near_lat = coords.lat; params.near_lng = coords.lng; if (zipState) params.near_zip = zipState; }
        else if (zipState) { params.near_zip = zipState; }
        api.get(`/trainers/${id}`, { params })
            .then((r) => setTrainer(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id, zipState, coords]);

    if (loading && !trainer) return <div className="text-center text-[#a0a0a0] py-20">Loading…</div>;
    if (notFound || !trainer) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="trainer-notfound">
                <div className="font-display text-4xl">Trainer not found</div>
                <button onClick={goBack} data-testid="not-found-back" className="text-[#2196f3] hover:underline text-sm mt-4 inline-block">← Back</button>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-10">
            {trainer.detail_photo && (
                <div className="relative mb-6 border border-[#2a2a2a] bg-[#0a0a0a] flex items-center justify-center overflow-hidden aspect-[16/9]">
                    <img
                        src={trainer.detail_photo}
                        alt={trainer.name}
                        data-testid="trainer-detail-photo"
                        className="w-full h-full object-cover"
                    />
                    {trainer.photo && (
                        <HeroLogoBadge src={trainer.photo} alt={trainer.name} fit="cover" testid="trainer-detail-logo-badge" />
                    )}
                </div>
            )}

            <div className="flex items-start gap-6">
                {!trainer.detail_photo && trainer.photo && (
                    <img
                        src={trainer.photo}
                        alt={trainer.name}
                        data-testid="trainer-photo"
                        className="h-28 w-28 border border-[#2a2a2a] object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-[#2196f3] text-xs uppercase tracking-[0.3em] mb-1">Trainer</div>
                    <h1 ref={nameFitRef} data-testid="trainer-name" className="font-display tracking-wide leading-none whitespace-nowrap overflow-hidden">{trainer.name}</h1>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-[#a0a0a0]">
                        <MapLink direct parts={[trainer.home_zip]} testid="trainer-location" className="inline-flex items-center gap-1.5 hover:text-[#2196f3] transition-colors"><MapPin className="h-4 w-4 text-[#2196f3]" />{trainer.home_zip}</MapLink>
                        <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-[#2196f3]" />{trainer.years_experience}y experience</span>
                        {trainer.hourly_rate != null && (
                            <span className="inline-flex items-center gap-1.5"><DollarSign className="h-4 w-4 text-[#2196f3]" />${trainer.hourly_rate}/hr</span>
                        )}
                        <DistanceZip distanceMiles={trainer.distance_miles} zip={zipState} onZip={handleZip} accent="#2196f3" testid="trainer-distance" />
                    </div>
                    <div className="mt-3"><FlagButton targetType="trainer" targetId={trainer.id} label="Report listing" /></div>
                </div>
                <div className="flex flex-col gap-2 items-stretch shrink-0">
                    <div className="flex items-stretch gap-2">
                    <Button
                        data-testid="follow-btn"
                        title={isFollowing("trainer", trainer.id) ? "Following" : "Follow"}
                        aria-label={isFollowing("trainer", trainer.id) ? "Following" : "Follow"}
                        disabled={followBusy}
                        onClick={async () => {
                            if (!user) return toast.error("Please log in to follow");
                            setFollowBusy(true);
                            const wasFollowing = isFollowing("trainer", trainer.id);
                            const res = await toggleFollow("trainer", trainer.id);
                            setFollowBusy(false);
                            if (res.ok) toast.success(wasFollowing ? "Unfollowed" : "You'll be notified when this trainer posts");
                        }}
                        className={`flex-1 rounded-none uppercase tracking-wider h-10 ${isFollowing("trainer", trainer.id) ? "bg-[#2196f3] hover:bg-[#1976d2] text-white" : "bg-[#141414] border border-[#2a2a2a] hover:border-[#2196f3]"}`}
                    >
                        {isFollowing("trainer", trainer.id)
                            ? <BookmarkCheck className="h-4 w-4 mr-2" />
                            : <BookmarkPlus className="h-4 w-4 mr-2" />}
                        {isFollowing("trainer", trainer.id) ? "Following" : "Follow"}
                    </Button>
                    {isFollowing("trainer", trainer.id) && (
                        <FollowAlertsBell targetType="trainer" targetId={trainer.id} size={18} />
                    )}
                    </div>
                    <ShareButton
                        testid="share-trainer-btn"
                        shareUrl={`${process.env.REACT_APP_BACKEND_URL}/api/share/trainer/${trainer.id}`}
                        title={`${trainer.name} · ${trainer.sport || "Sports"} Trainer`}
                        description={`${(trainer.specialties || []).slice(0, 3).join(", ")} · ${trainer.years_experience}y experience`}
                    />
                </div>
            </div>

            <div className="h-px bg-[#2a2a2a] my-8" />

            <section>
                <h2 className="font-display text-2xl tracking-wide mb-3">Specialties</h2>
                <div className="flex flex-wrap gap-2" data-testid="trainer-specialties">
                    {(trainer.specialties || []).length > 0 ? (
                        trainer.specialties.map((s) => (
                            <span key={s} className="text-xs uppercase tracking-wider px-3 py-2 bg-[#141414] border border-[#2a2a2a]">
                                <Dumbbell className="h-3 w-3 inline mr-1.5 text-[#2196f3]" />{s}
                            </span>
                        ))
                    ) : (
                        <span className="text-sm text-[#a0a0a0]">No specialties listed</span>
                    )}
                </div>
            </section>

            {trainer.bio && (
                <section className="mt-8">
                    <h2 className="font-display text-2xl tracking-wide mb-3">About</h2>
                    <p data-testid="trainer-bio" className="text-sm text-[#cfcfcf] leading-relaxed whitespace-pre-line">{trainer.bio}</p>
                </section>
            )}

            {trainer.socials && (
                <section className="mt-8">
                    <h2 className="font-display text-2xl tracking-wide mb-3">Follow</h2>
                    <SocialsDisplay socials={trainer.socials} testIdPrefix="trainer-socials-display" />
                </section>
            )}

            <MessageBoard entityType="trainer" entityId={trainer.id} accent="#2196f3" />
            <CollaboratorsManager entityType="trainer" entityId={trainer.id} accent="#2196f3" canManage={!!(user && (user.is_admin || user.id === trainer.id))} />
        </div>
    );
}
